import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message } from "antd";
import * as analyticsApi from "../api/analyticsApi";
import { BoardCard } from "./BoardViewPage";
import {
  normalizeCardsOnLoad,
  computeOrderedRows,
  computeRowHeights,
  BASE_ROW_HEIGHT_PX,
} from "../utils/boardLayoutUtils";
import { cryptiqueAnalyticsTheme as theme } from "../theme";

export default function PublicBoardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardResults, setCardResults] = useState({});

  const loadBoard = async () => {
    if (!token) {
      message.error("Invalid board link");
      navigate("/");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsApi.getPublicBoard(token);
      const data = response?.data;
      if (!data) {
        message.error("Board not found or expired");
        navigate("/");
        return;
      }
      setBoard(data);
    } catch (err) {
      console.error("Error loading public board:", err);
      message.error("Failed to load board. It may have expired or been removed.");
      setError(err.message || "Failed to load board");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoard();
  }, [token, navigate]);

  const executableTypes = ["insights", "funnel"];
  useEffect(() => {
    if (!board?.cards?.length || !token) return;
    board.cards.forEach((card) => {
      const type = (card?.cardType || "").toLowerCase();
      if (!executableTypes.includes(type)) return;
      analyticsApi
        .executeCardPublic(token, card.id)
        .then((res) => {
          const result = res?.data != null ? res.data : res;
          setCardResults((prev) => ({ ...prev, [card.id]: result }));
        })
        .catch((err) => {
          setCardResults((prev) => ({
            ...prev,
            [card.id]: { error: err?.message || "Failed to load" },
          }));
        });
    });
  }, [board?.cards, token]);

  // Same layout algorithm as BoardViewPage: single source of truth from saved board
  const normalizedCards = useMemo(
    () => normalizeCardsOnLoad(board?.cards || []),
    [board?.cards]
  );
  const orderedRows = useMemo(
    () => computeOrderedRows(normalizedCards),
    [normalizedCards]
  );
  const rowHeights = useMemo(
    () => computeRowHeights(orderedRows),
    [orderedRows]
  );

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="text-sm" style={{ color: theme.textMuted }}>
          Loading board...
        </div>
      </div>
    );
  }

  if (error || !board) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="text-sm" style={{ color: "#ef4444" }}>
          {error || "Board not found"}
        </div>
      </div>
    );
  }

  return (
    <div
      className="public-board-page flex flex-col h-screen"
      style={{
        backgroundColor: "#ffffff",
        fontFamily: "Raleway, sans-serif",
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        className="flex-shrink-0 px-6 py-4 border-b"
        style={{
          borderColor: theme.border,
          backgroundColor: "#fafafa",
          minWidth: 0,
        }}
      >
        <p className="text-xs mb-1" style={{ color: theme.textMuted }}>
          Viewing shared board
        </p>
        <h1 className="text-xl font-semibold truncate" style={{ color: theme.textPrimary }}>
          {board.name || "Untitled"}
        </h1>
        {board.description ? (
          <p className="text-sm mt-1 truncate" style={{ color: theme.textSecondary }}>
            {board.description}
          </p>
        ) : null}
      </div>

      <main
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6 pl-4 pr-6"
        style={{ width: "100%", maxWidth: "100%" }}
      >
        <div
          className="board-grid flex flex-col w-full"
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            padding: 0,
            minWidth: 0,
            gap: "24px",
          }}
        >
          {orderedRows.map((row, rowIndex) => {
            const rowKey = row.map((c) => c.id).join("-");
            const rowHeightMultiplier = rowHeights[rowKey] ?? 1;
            const rowHeightPx = Math.round(BASE_ROW_HEIGHT_PX * rowHeightMultiplier);
            const defaultW = 1 / Math.max(1, row.length);

            return (
              <div key={rowKey} className="flex flex-col" style={{ minWidth: 0 }}>
                <div
                  style={{
                    height: rowHeightPx,
                    minHeight: rowHeightPx,
                    maxHeight: rowHeightPx,
                    overflow: "hidden",
                    flexShrink: 0,
                    flexGrow: 0,
                    width: "100%",
                    maxWidth: "100%",
                  }}
                  className="flex flex-col"
                >
                  <div
                    className="flex-1 min-w-0 min-h-0 flex flex-row items-stretch rounded-lg gap-2"
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    {row.map((card, orderInRow) => {
                      const cardType = (card?.cardType || "insights").toLowerCase();
                      const isBorderedType =
                        cardType === "insights" || cardType === "funnel";
                      const cardW =
                        row.length === 1
                          ? 1
                          : typeof card.position?.w === "number"
                            ? card.position.w
                            : defaultW;

                      return (
                        <div
                          key={card.id}
                          className="min-w-0 h-full flex flex-row items-stretch"
                          style={{
                            flexBasis: 0,
                            flexGrow: cardW,
                            flexShrink: cardW,
                            minWidth: 0,
                            maxWidth: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            className={`rounded-xl overflow-hidden w-full relative pl-6 h-full flex-1 min-h-0 flex flex-col ${isBorderedType ? "border border-gray-200 bg-white" : "border border-transparent bg-transparent"}`}
                            style={{
                              minWidth: 0,
                              maxWidth: "100%",
                              ...(isBorderedType ? {} : { backgroundColor: "transparent" }),
                            }}
                          >
                            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                              <BoardCard
                                card={card}
                                result={cardResults[card.id]}
                                onSaveTextContent={undefined}
                                onSaveMediaUrl={undefined}
                                onSaveMediaDisplayOptions={undefined}
                                boardId={board.id}
                                rowHeightPx={rowHeightPx}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
